import useSWR from 'swr';
import { useSystemConfig } from '../lib/system-config';

interface LogisticsIntel {
    status: 'ACTIVE' | 'LOCATION_REQUIRED' | 'LOADING' | 'ERROR';
    weather: {
        temperature: number;
        condition: string;
        isStorm: boolean;
        windSpeed: number;
    } | null;
    transit: {
        delayMinutes: number;
        reason: string;
        impactLevel: 'NONE' | 'MODERATE' | 'SEVERE';
    } | null;
}

const fetcher = async (url: string) => {
    const res = await fetch(url);
    if (!res.ok) throw new Error('Fetch failed');
    return res.json();
};

export function useLogisticsIntel(): LogisticsIntel {
    // Authoritative settings from SystemConfig
    const { config } = useSystemConfig();

    const isMetric = config.units.distance === 'KM';

    const lat = config.facility.latitude ? parseFloat(config.facility.latitude) : NaN;
    const lng = config.facility.longitude ? parseFloat(config.facility.longitude) : NaN;
    const hasLocation = Number.isFinite(lat) && Number.isFinite(lng);

    // Always compute URL, but only enable SWR when we have valid location
    const windUnit = isMetric ? 'kmh' : 'mph';
    const tempUnit = 'fahrenheit'; // keep existing behavior; metric can be added later if desired

    const weatherUrl = hasLocation
        ? `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current=temperature_2m,weather_code,wind_speed_10m&temperature_unit=${tempUnit}&wind_speed_unit=${windUnit}`
        : null;

    // IMPORTANT: hook must be called unconditionally
    const { data: weatherData, error: weatherError } = useSWR(
        weatherUrl,
        fetcher,
        { refreshInterval: 300000 }
    );

    // If no configured facility location, show LOCATION_REQUIRED (no fetch performed because key is null)
    if (!hasLocation) {
        return {
            status: 'LOCATION_REQUIRED',
            weather: null,
            transit: null
        };
    }

    if (weatherError) {
        return { status: 'ERROR', weather: null, transit: null };
    }

    if (!weatherData) {
        return { status: 'LOADING', weather: null, transit: null };
    }

    const calculateDelay = (code: number, wind: number) => {
        let delay = 0;
        let reason = 'Optimal Conditions';
        let impact: 'NONE' | 'MODERATE' | 'SEVERE' = 'NONE';

        if (code >= 95) { delay += 45; reason = 'Thunderstorm'; impact = 'SEVERE'; }
        else if (code >= 71) { delay += 30; reason = 'Snowfall'; impact = 'SEVERE'; }
        else if (code >= 51) { delay += 15; reason = 'Rain'; impact = 'MODERATE'; }
        else if (code >= 45) { delay += 10; reason = 'Fog'; impact = 'MODERATE'; }

        if (wind > (isMetric ? 40 : 25)) {
            delay += 15;
            reason = reason === 'Optimal Conditions' ? 'High Winds' : `${reason} + Winds`;
            impact = 'SEVERE';
        }

        return { delayMinutes: delay, reason, impactLevel: impact };
    };

    const parseCondition = (code: number) => {
        let text = 'Clear';
        let isStorm = false;

        if (code === 0) text = 'Clear Sky';
        else if (code >= 1 && code <= 3) text = 'Partly Cloudy';
        else if (code >= 45 && code <= 48) text = 'Foggy';
        else if (code >= 51 && code <= 67) text = 'Rainy';
        else if (code >= 71 && code <= 77) text = 'Snowy';
        else if (code >= 80 && code <= 82) text = 'Heavy Rain';
        else if (code >= 95) { text = 'Thunderstorm'; isStorm = true; }

        return { text, isStorm };
    };

    const current = weatherData.current;
    const weatherCode = current.weather_code !== undefined ? current.weather_code : 0;
    const { text, isStorm } = parseCondition(weatherCode);

    const { delayMinutes, reason, impactLevel } = calculateDelay(weatherCode, current.wind_speed_10m);

    return {
        status: 'ACTIVE',
        weather: {
            temperature: Math.round(current.temperature_2m),
            condition: text,
            isStorm: isStorm || current.wind_speed_10m > (isMetric ? 32 : 20),
            windSpeed: current.wind_speed_10m
        },
        transit: {
            delayMinutes,
            reason,
            impactLevel
        }
    };
}
